(() => {
  'use strict';

  const OPTION_LABEL_SELECTOR = [
    '.product-card__variants--list span',
    '.product-card__variants--list-info span',
    '.product-card__variants--list-info-mobile span'
  ].join(',');

  const OPEN_BOX = {
    src: 'https://cdn.shopify.com/s/files/1/0716/8462/0517/files/BF9659FF-6C67-4B43-A36C-3AAAD15443DA.png?v=1786945273',
    alt: 'Aurelia gift box opened, showing the gold script inside the lid, cotton dust bag, pink heart confetti, jewellery box and hand-addressed thank-you card',
    width: 1122,
    height: 1402
  };

  const HERO_IMAGE_ALTS = {
    'A5D89837-6B0D-422F-8DC6-9554A6AD483F.png':
      'Aurelia — model walking a rain-washed street beneath an oversized pink bow umbrella, wearing a navy denim bralette and wide-leg trousers with pink and butter-yellow sash bows',
    '33D98094-10F7-488F-9A26-A7DFE4D86F32.png':
      'Three Aurelia looks — navy denim set with sash bows, mint butterfly-appliqué dress and lilac polka dot gown, in a courtyard of pastel archways'
  };

  const collectMatches = (root, selector) => {
    if (!(root instanceof Element) && root !== document) return [];
    const matches = [];
    if (root instanceof Element && root.matches(selector)) matches.push(root);
    matches.push(...root.querySelectorAll(selector));
    return matches;
  };

  const normalizeProductOptionLabels = (root = document) => {
    collectMatches(root, OPTION_LABEL_SELECTOR).forEach((label) => {
      const text = label.textContent.replace(/\s+/g, ' ').trim();
      const match = text.match(/^(\d+)\s+(SIZE|SIZES|COLOR|COLORS)$/i);
      if (!match) return;

      const count = Number(match[1]);
      const base = match[2].toLowerCase().startsWith('color') ? 'color' : 'size';
      const corrected = `${count} ${base}${count === 1 ? '' : 's'}`;
      if (label.textContent !== corrected) label.textContent = corrected;
    });
  };

  const preserveHeroImageMetadata = (root = document) => {
    collectMatches(root, '#shopify-section-1658995208ad05f95f .hero__media img').forEach((image) => {
      const source = [image.currentSrc, image.src, image.srcset].filter(Boolean).join(' ');
      const filename = Object.keys(HERO_IMAGE_ALTS).find((candidate) => source.includes(candidate));
      if (!filename) return;

      image.alt = HERO_IMAGE_ALTS[filename];
      image.loading = 'eager';
      image.setAttribute('fetchpriority', 'high');
      image.decoding = 'async';
    });
  };

  const removeRedundantHeroMonogram = (root = document) => {
    collectMatches(root, '#shopify-section-1658995208ad05f95f .aurelia-monogram--hero')
      .forEach((monogram) => monogram.remove());
  };

  const replaceBoxTeaserImage = (root = document) => {
    collectMatches(root, '.aurelia-box-teaser__media img').forEach((image) => {
      if (image.dataset.aureliaOpenBox === 'true') return;

      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = OPEN_BOX.src;
      image.alt = OPEN_BOX.alt;
      image.width = OPEN_BOX.width;
      image.height = OPEN_BOX.height;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.dataset.aureliaOpenBox = 'true';
    });
  };

  const normalizeMobileProductGrids = (root = document) => {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    collectMatches(root, '.template--index .section-featured-products .swiper.card-product-slider')
      .forEach((slider) => {
        slider.style.setProperty('width', '100%', 'important');
        slider.style.setProperty('max-width', '100%', 'important');
        slider.style.setProperty('margin-inline', '0', 'important');
      });

    collectMatches(root, '.template--index .section-featured-products .swiper-wrapper.card-product-slider__wrapper')
      .forEach((wrapper) => {
        wrapper.style.setProperty('display', 'grid', 'important');
        wrapper.style.setProperty('grid-template-columns', 'repeat(2, minmax(0, 1fr))', 'important');
        wrapper.style.setProperty('width', '100%', 'important');
        wrapper.style.setProperty('max-width', '100%', 'important');
        wrapper.style.setProperty('margin', '0', 'important');
        wrapper.style.setProperty('transform', 'none', 'important');
      });

    collectMatches(root, '.template--index .section-featured-products .swiper-slide.card-product-slider__slide')
      .forEach((slide) => {
        slide.style.setProperty('width', '100%', 'important');
        slide.style.setProperty('min-width', '0', 'important');
        slide.style.setProperty('margin', '0', 'important');
      });

    collectMatches(root, '.template--index .section-featured-products .product-card__media img')
      .forEach((image) => {
        image.style.setProperty('object-position', 'center center', 'important');
      });
  };

  const enforceCollectionOnlyAnnouncement = () => {
    if (!document.body || document.body.classList.contains('template--collection')) return;

    document.querySelectorAll('.section-announcement-bar').forEach((bar) => {
      bar.hidden = true;
      bar.setAttribute('aria-hidden', 'true');
      bar.style.setProperty('display', 'none', 'important');
    });
  };

  const forceFreeSaudiShipping = () => {
    if (window.theme && window.theme.settings) {
      window.theme.settings.cart_free_shipping_threshold = 0;
    }
  };

  const run = (root = document) => {
    normalizeProductOptionLabels(root);
    preserveHeroImageMetadata(root);
    removeRedundantHeroMonogram(root);
    replaceBoxTeaserImage(root);
    normalizeMobileProductGrids(root);
    enforceCollectionOnlyAnnouncement();
    forceFreeSaudiShipping();
  };

  const queueFullPass = () => {
    [0, 120, 500, 1200].forEach((delay) => {
      window.setTimeout(() => run(document), delay);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', queueFullPass, { once: true });
  } else {
    queueFullPass();
  }

  window.addEventListener('load', queueFullPass, { once: true });
  window.addEventListener('resize', () => run(document), { passive: true });
  document.addEventListener('shopify:section:load', (event) => run(event.target));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) run(node);
      });
    });

    enforceCollectionOnlyAnnouncement();
    forceFreeSaudiShipping();
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
